import {
  AlertTriangle,
  ArrowRight,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Clock,
  Gift,
  Plus,
  X,
} from 'lucide-react';
import { useEffect, useState } from 'react';

import {
  type Availability,
  type Birthday,
  type Booking,
  birthdayDate,
  type CalendarItem,
  calendarZone,
  dayRange,
  freeSlots,
  historyForMove,
  type Interval,
  localDay,
  localInput,
  localInstant,
  overlaps,
  reminderList,
  reservedMinutes,
  shiftDay,
  validateCalendarState,
} from '../../shared/calendar';
import type { LocalRecord, Note, Task } from '../../shared/model';
import type { Person } from '../../shared/productivity';
import { repeatBookings } from '../../shared/recurrence';
import { isAvailableRecord } from '../../shared/trash';

import { workspace } from '../lib/engine';
import { taskStone } from '../lib/stones';
import { DayBar } from './SetOut';
import { StatusBadge } from './StatusBadge';
import {
  ActionIconButton,
  Button,
  Disclosure,
  IconButton,
  Input,
  Modal,
  PageHeader,
  Select,
  Textarea,
} from './ui';

const time = (iso: string) =>
  new Date(iso).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: calendarZone(),
  });

type Draft = {
  id: string;
  type: 'booking' | 'birthday';
  title: string;
  start: string;
  end: string;
  taskId: string;
  noteId: string;
  notes: string;
  reminder: number;
  month: number;
  day: number;
  personId: string;
  frequency: '' | 'daily' | 'weekly' | 'monthly' | 'yearly';
  repeatCount: number;
  repeatInterval: number;
  recurrence?: Booking['recurrence'];
  followUpTaskIds: string[];
  history: Interval[];
  cancelled: boolean;
};

function blank(day: string): Draft {
  return {
    id: crypto.randomUUID(),
    personId: '',
    frequency: '',
    repeatCount: 12,
    repeatInterval: 1,
    followUpTaskIds: [],
    type: 'booking',
    title: '',
    start: `${day}T09:00`,
    end: `${day}T09:30`,
    taskId: '',
    noteId: '',
    notes: '',
    reminder: 15,
    month: Number(day.slice(5, 7)),
    day: Number(day.slice(8)),
    history: [],
    cancelled: false,
  };
}

export function CalendarReminders({ records, open }: { records: LocalRecord[]; open: () => void }) {
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 30000);

    return () => clearInterval(timer);
  }, []);

  const reminders = reminderList(records, now);

  if (!reminders.length) {
    return null;
  }

  return (
    <button type="button" data-ui="specialized" className="calendar-reminder-banner" onClick={open}>
      <Clock size={15} />
      <span>
        <strong>{reminders[0].title}</strong> · {reminders[0].detail}
        {reminders.length > 1 ? ` · ${reminders.length - 1} more` : ''}
      </span>
      <ArrowRight size={14} />
    </button>
  );
}

export function CalendarPage({
  records,
  openTask,
  openNote,
}: {
  records: LocalRecord[];
  openTask: (id: string) => void;
  openNote: (id: string) => void;
}) {
  const zone = calendarZone();

  const [day, setDay] = useState(localDay()),
    [draft, setDraft] = useState<Draft | null>(null),
    [error, setError] = useState(''),
    [saving, setSaving] = useState(false),
    [notice, setNotice] = useState('');

  const [duration, setDuration] = useState(30),
    [occurrence, setOccurrence] = useState<'' | 'earlier' | 'later'>('');

  const [availabilityOpen, setAvailabilityOpen] = useState(false),
    [windowDraft, setWindowDraft] = useState<{ start: string; end: string }[]>([]),
    [cancelledView, setCancelledView] = useState(false);

  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30000);

    return () => clearInterval(timer);
  }, []);

  const calendar = records.filter(r => r.kind === 'calendar');

  const bookings = calendar.filter(
    r => (r.value as CalendarItem).type === 'booking',
  ) as (LocalRecord & { value: Booking })[];

  const birthdays = calendar.filter(
    r => (r.value as CalendarItem).type === 'birthday',
  ) as (LocalRecord & { value: Birthday })[];

  const availability = calendar.find(r => {
    const v = r.value as CalendarItem;

    return v.type === 'availability' && v.day === day;
  }) as (LocalRecord & { value: Availability }) | undefined;

  const windows = availability?.value.windows ?? [],
    range = dayRange(day);

  const tasks = records.filter(r => r.kind === 'task' && isAvailableRecord(r)),
    notes = records.filter(r => r.kind === 'note' && isAvailableRecord(r));

  const events = bookings
    .filter(r => r.value.cancelled === cancelledView && overlaps(r.value, range))
    .sort((a, b) => a.value.startsAt.localeCompare(b.value.startsAt));

  const historical = !cancelledView
    ? bookings.flatMap(r =>
        r.value.history
          .filter(h => overlaps(h, range))
          .map((h, i) => ({
            id: `${r.id}:${i}`,
            title: r.value.title,
            interval: h,
            taskId: r.value.taskId,
          })),
      )
    : [];

  const upcomingBirthdays = birthdays.filter(r => !r.value.cancelled);

  const dailyBirthdays = birthdays.filter(
    r =>
      r.value.cancelled === cancelledView && birthdayDate(r.value, Number(day.slice(0, 4))) === day,
  );

  const reminders = reminderList(records, new Date(now));

  const missed = bookings.filter(
    r =>
      !r.value.cancelled &&
      tasks.some(t => t.id === r.value.taskId) &&
      Date.parse(r.value.endsAt) < now &&
      !(tasks.find(t => t.id === r.value.taskId)?.value as Task | undefined)?.done,
  );

  let calendarIssue = '';

  try {
    validateCalendarState(records, now);
  } catch (e) {
    calendarIssue = (e as Error).message;
  }

  const suggestions =
    draft?.type === 'booking'
      ? freeSlots(
          windows,
          bookings.filter(r => !r.value.cancelled && r.id !== draft.id).map(r => r.value),
          duration,
          Math.max(now, Date.parse(range.startsAt)),
        )
      : [];

  function edit(record: LocalRecord, reschedule = false) {
    const value = record.value as CalendarItem;

    setError('');
    setNotice('');
    setOccurrence('');

    if (value.type === 'availability') {
      return;
    }

    if (value.type === 'birthday') {
      setDraft({
        ...blank(day),
        id: record.id,
        type: 'birthday',
        title: value.title,
        month: value.month,
        day: value.day,
        notes: value.notes,
        personId: value.personId ?? '',
        reminder: value.reminderDays,
        cancelled: value.cancelled,
      });

      return;
    }

    setDuration(Math.round((Date.parse(value.endsAt) - Date.parse(value.startsAt)) / 60000));

    setDraft({
      ...blank(day),
      id: record.id,
      title: value.title,
      start: reschedule ? `${day}T09:00` : localInput(value.startsAt),
      end: reschedule
        ? localInput(
            new Date(
              new Date(`${day}T09:00`).getTime() +
                Date.parse(value.endsAt) -
                Date.parse(value.startsAt),
            ).toISOString(),
          )
        : localInput(value.endsAt),
      taskId: value.taskId ?? '',
      noteId: value.noteId ?? '',
      notes: value.notes,
      reminder: value.reminderMinutes,
      cancelled: value.cancelled,
      history: value.history,
      personId: value.personId ?? '',
      recurrence: value.recurrence,
      followUpTaskIds: value.followUpTaskIds ?? [],
    });
  }

  async function commit(value: CalendarItem, id: string) {
    validateCalendarState(
      [...records.filter(r => r.id !== id), { id, kind: 'calendar', value }],
      now,
    );

    await workspace.save(id, 'calendar', value);
  }

  async function saveDraft() {
    if (!draft) {
      return;
    }

    setSaving(true);
    setError('');

    try {
      const original = bookings.find(r => r.id === draft.id)?.value;
      let value: CalendarItem;

      if (draft.type === 'birthday') {
        value = {
          type: 'birthday',
          title: draft.title,
          personId: draft.personId || null,
          month: draft.month,
          day: draft.day,
          notes: draft.notes,
          reminderDays: draft.reminder,
          cancelled: draft.cancelled,
        };
      } else {
        const startsAt = localInstant(draft.start, occurrence),
          endsAt = localInstant(draft.end, occurrence);

        if (endsAt <= startsAt) {
          throw new Error('End must be after start.');
        }

        if (draft.taskId && (Date.parse(endsAt) - Date.parse(startsAt)) / 60000 !== duration) {
          throw new Error(
            'The reserved time must match the estimate. Adjust the end time or estimate.',
          );
        }

        const history = historyForMove(original, { startsAt, endsAt }, now);

        value = {
          type: 'booking',
          zone,
          personId: draft.personId || null,
          followUpTaskIds: draft.followUpTaskIds,
          ...(draft.recurrence ? { recurrence: draft.recurrence } : {}),
          title: draft.title,
          startsAt,
          endsAt,
          taskId: draft.taskId || null,
          noteId: draft.noteId || null,
          notes: draft.notes,
          reminderMinutes: draft.reminder,
          cancelled: draft.cancelled,
          history,
        };
      }

      if (value.type === 'booking') {
        const bookings =
          draft.frequency && !draft.taskId && !draft.recurrence
            ? repeatBookings(
                value,
                draft.frequency,
                draft.repeatInterval,
                draft.repeatCount,
                zone,
                occurrence,
              )
            : [value];

        const changes = bookings.map((booking, i) => ({
          id: i === 0 ? draft.id : crypto.randomUUID(),
          kind: 'calendar' as const,
          value: booking,
        }));

        const task = records.find(r => r.id === draft.taskId);

        await workspace.saveMany(
          task
            ? [
                ...changes,
                {
                  ...task,
                  value: {
                    ...(task.value as Task),
                    estimateMinutes: duration,
                    today: localDay(new Date(value.startsAt)) === localDay(),
                  },
                },
              ]
            : changes,
        );
      } else {
        await commit(value, draft.id);
      }

      setDraft(null);
      setNotice('Saved on this device. The sync indicator shows server status.');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function setCancelled(record: LocalRecord, cancelled: boolean) {
    try {
      const value = record.value as Booking | Birthday;

      await commit({ ...value, cancelled }, record.id);
      setNotice(cancelled ? 'Cancelled. You can restore it from Cancelled items.' : 'Restored.');
      setDraft(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }

  function beginAvailability() {
    setWindowDraft(
      windows.length
        ? windows.map(w => ({ start: localInput(w.startsAt), end: localInput(w.endsAt) }))
        : [{ start: `${day}T09:00`, end: `${day}T17:00` }],
    );

    setAvailabilityOpen(true);
    setError('');
    setOccurrence('');
  }

  async function saveAvailability() {
    setSaving(true);
    setError('');

    try {
      const value: Availability = {
        type: 'availability',
        day,
        zone,
        windows: windowDraft.map(w => ({
          startsAt: localInstant(w.start, occurrence),
          endsAt: localInstant(w.end, occurrence),
        })),
      };

      if (
        value.windows.some(
          w =>
            localDay(new Date(w.startsAt)) !== day ||
            Date.parse(w.endsAt) > Date.parse(range.endsAt),
        )
      ) {
        throw new Error('Keep each window within the selected day.');
      }

      await commit(
        value,
        availability?.id ?? `ca1e0000-0000-4000-8000-${day.replaceAll('-', '').padStart(12, '0')}`,
      );

      setAvailabilityOpen(false);
      setNotice('Availability saved.');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  function startEvent() {
    setDraft(blank(day));
    setError('');
    setOccurrence('');
    setDuration(30);
  }

  return (
    <main className="calendar-page">
      <PageHeader
        eyebrow="MAKE ROOM FOR YOUR DAY"
        title="Your calendar"
        description="Manual plans, with a little breathing room."
        actions={<ActionIconButton action="event" label="Add event" onClick={startEvent} />}
      />
      <div className="calendar-navigation">
        <div className="button-row">
          <IconButton label="Previous day" onClick={() => setDay(shiftDay(day, -1))}>
            <ChevronLeft size={18} />
          </IconButton>
          <Input
            type="date"
            required
            aria-label="Calendar day"
            value={day}
            onInput={e => {
              if (e.currentTarget.value) {
                setDay(e.currentTarget.value);
              }
            }}
          />
          <IconButton label="Next day" onClick={() => setDay(shiftDay(day, 1))}>
            <ChevronRight size={18} />
          </IconButton>
          <Button variant="secondary" onClick={() => setDay(localDay())}>
            Today
          </Button>
        </div>
        <span className="small-muted">Times in {zone}</span>
      </div>
      <div className="calendar-week">
        {Array.from({ length: 7 }, (_, i) => shiftDay(day, i - 3)).map(date => (
          <button
            type="button"
            data-ui="specialized"
            key={date}
            className={date === day ? 'selected' : ''}
            onClick={() => setDay(date)}
          >
            <span>{new Date(`${date}T12:00:00`).toLocaleDateString([], { weekday: 'short' })}</span>
            <strong>{Number(date.slice(8))}</strong>
            <span className="calendar-day-dot">
              {bookings.some(r => !r.value.cancelled && overlaps(r.value, dayRange(date)))
                ? '•'
                : ' '}
            </span>
          </button>
        ))}
      </div>
      {calendarIssue && (
        <div className="calendar-conflict" role="alert">
          <AlertTriangle size={18} />
          <div>
            <strong>Resolve a scheduling conflict</strong>
            <p>{calendarIssue}</p>
            <p>Pending changes stay on this device. Open and move or cancel a conflicting item.</p>
            {bookings
              .filter(r => !r.value.cancelled)
              .map(r => (
                <Button variant="ghost" key={r.id} onClick={() => edit(r)}>
                  {r.value.title} · {localDay(new Date(r.value.startsAt))}
                </Button>
              ))}
          </div>
        </div>
      )}
      {notice && (
        <p className="notice" role="status">
          {notice}
        </p>
      )}
      {error && !draft && !availabilityOpen && (
        <p className="error-message" role="alert">
          {error}
        </p>
      )}
      <div className="calendar-layout">
        <section className="day-agenda">
          <div className="section-heading">
            <h2>
              {new Date(`${day}T12:00:00`).toLocaleDateString([], {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
              })}
            </h2>
            <Button variant="ghost" onClick={() => setCancelledView(!cancelledView)}>
              {cancelledView ? 'Back to schedule' : 'Cancelled items'}
            </Button>
          </div>
          <div className="availability-card">
            <div>
              <Clock size={16} />
              <strong>Your availability</strong>
            </div>
            <p>
              {windows.length
                ? windows.map(w => `${time(w.startsAt)}–${time(w.endsAt)}`).join(' · ')
                : 'Choose when you have time for tasks.'}
            </p>
            {windows.length > 0 && (
              <DayBar
                day={day}
                windows={windows}
                blocks={bookings
                  .filter(
                    r =>
                      !r.value.cancelled &&
                      r.value.startsAt < dayRange(day).endsAt &&
                      r.value.endsAt > dayRange(day).startsAt,
                  )
                  .map(r => {
                    const task = r.value.taskId
                      ? tasks.find(t => t.id === r.value.taskId)
                      : undefined;

                    return {
                      id: r.id,
                      from: Date.parse(r.value.startsAt),
                      to: Date.parse(r.value.endsAt),
                      style: task ? ('reserved' as const) : ('appointment' as const),
                      mineral: task ? taskStone(task.id, task.value as Task).mineral : undefined,
                    };
                  })}
                summary={`Available ${windows.map(w => `${time(w.startsAt)} to ${time(w.endsAt)}`).join(', ')}.`}
              />
            )}
            <Button variant="ghost" onClick={beginAvailability}>
              {windows.length ? 'Edit windows' : 'Set availability'}{' '}
            </Button>
          </div>
          {tasks
            .filter(r => !(r.value as Task).dropped && (r.value as Task).dueDate === day)
            .map(r => {
              // A deadline is all-day, so it says what else the day holds for
              // the same task: reserved time sits further down the agenda, and
              // claiming there is none while it is on screen is worse than
              // saying nothing.
              const reserved = reservedMinutes(
                bookings.map(booking => booking.value),
                r.id,
                range,
              );

              return (
                <button
                  type="button"
                  data-ui="specialized"
                  className={`birthday-card deadline-card ${(r.value as Task).done ? 'deadline-complete' : day < localDay(new Date(now)) ? 'deadline-overdue' : ''}`}
                  key={r.id}
                  onClick={() => openTask(r.id)}
                >
                  <Clock size={16} />
                  <span>
                    <strong>{(r.value as Task).title}</strong>
                    <small>
                      Deadline ·{' '}
                      {(r.value as Task).done
                        ? 'completed'
                        : `${day < localDay(new Date(now)) ? 'overdue' : 'all day'} · ${
                            reserved ? `${reserved} min reserved` : 'no reserved time'
                          }`}
                    </small>
                  </span>
                </button>
              );
            })}
          {dailyBirthdays.map(record => (
            <button
              type="button"
              data-ui="specialized"
              className="birthday-card"
              key={record.id}
              onClick={() => edit(record)}
            >
              <Gift size={18} />
              <span>
                <strong>{record.value.title}</strong>
                <small>Birthday · all day</small>
              </span>
              <ChevronRight size={16} />
            </button>
          ))}
          <div className="agenda-list">
            {events.map(record => (
              <article
                className={`agenda-event ${record.value.taskId ? 'task-booking' : ''} ${record.value.cancelled ? 'cancelled' : ''}`}
                key={record.id}
              >
                <div className="agenda-time">
                  <strong>{time(record.value.startsAt)}</strong>
                  <span>{time(record.value.endsAt)}</span>
                </div>
                <div className="agenda-event-body">
                  <button
                    type="button"
                    data-ui="specialized"
                    className="agenda-title"
                    onClick={() => edit(record)}
                  >
                    {record.value.title}
                  </button>
                  <p>
                    <StatusBadge
                      tone={
                        record.value.cancelled
                          ? 'neutral'
                          : record.value.taskId
                            ? 'priority'
                            : 'info'
                      }
                    >
                      {record.value.cancelled
                        ? 'Cancelled'
                        : record.value.taskId
                          ? 'Task reservation'
                          : 'Appointment'}
                    </StatusBadge>{' '}
                    ·{' '}
                    {Math.round(
                      (Date.parse(record.value.endsAt) - Date.parse(record.value.startsAt)) / 60000,
                    )}{' '}
                    min{record.operationId ? ' · Pending sync' : ''}
                  </p>
                  {record.value.notes && <p className="agenda-notes">{record.value.notes}</p>}
                  <div className="button-row wrap">
                    {tasks.some(t => t.id === record.value.taskId) && (
                      <Button variant="ghost" onClick={() => openTask(record.value.taskId!)}>
                        Open task{' '}
                      </Button>
                    )}
                    {(record.value.followUpTaskIds ?? [])
                      .filter(id => tasks.some(t => t.id === id))
                      .map(id => (
                        <Button variant="ghost" key={id} onClick={() => openTask(id)}>
                          Open follow-up
                        </Button>
                      ))}
                    {!record.value.taskId && (
                      <ActionIconButton
                        action="task"
                        label="Create follow-up"
                        onClick={async () => {
                          const id = crypto.randomUUID();

                          try {
                            await workspace.saveMany([
                              {
                                id,
                                kind: 'task',
                                value: {
                                  title: `Follow up: ${record.value.title}`,
                                  noteId: record.value.noteId,
                                  personId: record.value.personId ?? null,
                                  done: false,
                                  today: false,
                                  steps: [],
                                  stoppingPoint: '',
                                },
                              },
                              {
                                ...record,
                                value: {
                                  ...record.value,
                                  followUpTaskIds: [...(record.value.followUpTaskIds ?? []), id],
                                },
                              },
                            ]);

                            openTask(id);
                          } catch (e) {
                            setError((e as Error).message);
                          }
                        }}
                      />
                    )}
                    {notes.some(n => n.id === record.value.noteId) && (
                      <Button variant="ghost" onClick={() => openNote(record.value.noteId!)}>
                        Working note
                      </Button>
                    )}
                    <Button variant="ghost" onClick={() => edit(record, true)}>
                      Reschedule
                    </Button>
                    {record.value.cancelled && (
                      <ActionIconButton
                        action="restore"
                        label="Restore"
                        onClick={() => void setCancelled(record, false)}
                      />
                    )}
                  </div>
                </div>
                <IconButton label={`Edit ${record.value.title}`} onClick={() => edit(record)}>
                  <ChevronRight size={16} />
                </IconButton>
              </article>
            ))}
          </div>
          {/* An abandoned plan is worth keeping and not worth reading every
              day, so the day says how many there were and folds them away.
              The caveat belongs to the set, not to each row. */}
          {!!historical.length && (
            <Disclosure
              className="historical-bookings"
              summary={`${historical.length} earlier reservation${historical.length === 1 ? '' : 's'} on this day`}
            >
              <p className="small-muted">
                Rescheduled after they had started. These record a plan, not time worked.
              </p>
              {historical.map(h => (
                <div className="historical-booking" key={h.id}>
                  <Clock size={14} />
                  <strong>
                    {time(h.interval.startsAt)}–{time(h.interval.endsAt)} · {h.title}
                  </strong>
                </div>
              ))}
            </Disclosure>
          )}
          {!events.length && !dailyBirthdays.length && !historical.length && (
            <div className="empty-state">
              <CalendarDays size={32} />
              <h2>{cancelledView ? 'Nothing cancelled on this day.' : 'A little open space.'}</h2>
              <p>
                {cancelledView
                  ? 'Cancelled items can be restored.'
                  : 'Add an appointment or reserve time for one of your priorities.'}
              </p>
              {!cancelledView && (
                <ActionIconButton action="event" label="Add event" onClick={startEvent} />
              )}
            </div>
          )}
        </section>
        <aside className="calendar-side">
          <section>
            <span className="eyebrow">AHEAD OF YOU</span>
            <h2>Reminders</h2>
            {reminders.length ? (
              reminders.map(r => (
                <button
                  type="button"
                  data-ui="specialized"
                  className="reminder-item"
                  key={r.id}
                  onClick={() => {
                    const entry = records.find(record => record.id === r.id);

                    if (entry) {
                      edit(entry);
                    }
                  }}
                >
                  <strong>{r.title}</strong>
                  {/* Underway is a different thing from approaching, and the
                      one worth catching at a glance. */}
                  {r.started ? (
                    <StatusBadge tone="priority">{r.detail}</StatusBadge>
                  ) : (
                    <span>{r.detail}</span>
                  )}
                </button>
              ))
            ) : (
              <p className="muted">Nothing needs a reminder right now.</p>
            )}
          </section>
          <section>
            <span className="eyebrow">CHOOSE A NEXT STEP</span>
            <h2>Reserve time</h2>
            {tasks
              .filter(r => !(r.value as Task).done && (r.value as Task).today)
              .map(record => {
                // Reserving more time for a task is legitimate; doing it
                // without being told the day already holds some is not.
                const reserved = reservedMinutes(
                  bookings.map(booking => booking.value),
                  record.id,
                  range,
                );

                return (
                  <button
                    type="button"
                    data-ui="specialized"
                    className="schedule-task"
                    key={record.id}
                    onClick={() => {
                      setDraft({
                        ...blank(day),
                        title: (record.value as Task).title,
                        taskId: record.id,
                        end: localInput(
                          new Date(
                            Date.parse(localInstant(`${day}T09:00`, 'earlier')) +
                              ((record.value as Task).estimateMinutes ?? 30) * 60000,
                          ).toISOString(),
                        ),
                        noteId: (record.value as Task).noteId ?? '',
                      });

                      setDuration((record.value as Task).estimateMinutes ?? 30);
                      setError('');
                      setOccurrence('');
                    }}
                  >
                    <span>
                      {(record.value as Task).title}
                      {reserved > 0 && <small>{reserved} min already reserved</small>}
                    </span>
                    {/* The plus invites a first block. A task the day already
                        holds time for keeps the row — reserving more is still
                        allowed — but not the invitation. */}
                    {reserved === 0 && <Plus size={14} />}
                  </button>
                );
              })}
          </section>
          {!!missed.length && (
            <section>
              <span className="eyebrow">PLANS THAT PASSED</span>
              <h2>Unfinished reservations</h2>
              {missed.map(record => (
                <div className="missed-booking" key={record.id}>
                  <strong>{record.value.title}</strong>
                  <small>
                    {localDay(new Date(record.value.startsAt))} · {time(record.value.startsAt)}
                  </small>
                  <Button variant="ghost" onClick={() => edit(record, true)}>
                    Find another time{' '}
                  </Button>
                </div>
              ))}
            </section>
          )}
          {/* A heading and a leap-year footnote over an empty list is a
              section about nothing; with no birthdays recorded there is
              nothing here to say. */}
          {!!upcomingBirthdays.length && (
            <section>
              <h2>Birthdays</h2>
              {upcomingBirthdays.map(r => (
                <button
                  type="button"
                  data-ui="specialized"
                  className="reminder-item"
                  key={r.id}
                  onClick={() => edit(r)}
                >
                  <strong>{r.value.title}</strong>
                  <span>
                    {r.value.month}/{r.value.day} · remind {r.value.reminderDays} days ahead
                  </span>
                </button>
              ))}
              <p className="small-muted">
                February 29 is observed on February 28 in non-leap years.
              </p>
            </section>
          )}
        </aside>
      </div>
      {(draft || availabilityOpen) && (
        <Modal
          open
          className="calendar-form"
          closeLabel="Close calendar editor"
          title={
            availabilityOpen
              ? 'Edit availability'
              : calendar.some(r => r.id === draft?.id)
                ? 'Edit event'
                : 'Add event'
          }
          onClose={() => {
            setDraft(null);
            setAvailabilityOpen(false);
            setError('');
          }}
          onSubmit={e => {
            e.preventDefault();
            void (availabilityOpen ? saveAvailability() : saveDraft());
          }}
        >
          {availabilityOpen ? (
            <>
              <p className="muted">
                {day} · {zone}. Task reservations must fit inside these windows.
              </p>
              {windowDraft.map((window, index) => (
                <div className="calendar-fields" key={index}>
                  <label>
                    From
                    <Input
                      aria-label={`Window ${index + 1} start`}
                      type="datetime-local"
                      required
                      value={window.start}
                      onInput={e =>
                        setWindowDraft(
                          windowDraft.map((w, i) =>
                            i === index ? { ...w, start: e.currentTarget.value } : w,
                          ),
                        )
                      }
                    />
                  </label>
                  <label>
                    Until
                    <Input
                      aria-label={`Window ${index + 1} end`}
                      type="datetime-local"
                      required
                      value={window.end}
                      onInput={e =>
                        setWindowDraft(
                          windowDraft.map((w, i) =>
                            i === index ? { ...w, end: e.currentTarget.value } : w,
                          ),
                        )
                      }
                    />
                  </label>
                  <IconButton
                    type="button"
                    label={`Remove window ${index + 1}`}
                    onClick={() => setWindowDraft(windowDraft.filter((_, i) => i !== index))}
                  >
                    <X size={15} />
                  </IconButton>
                </div>
              ))}
              <ActionIconButton
                action="add"
                label="Add a window"
                type="button"
                onClick={() =>
                  setWindowDraft([...windowDraft, { start: `${day}T18:00`, end: `${day}T20:00` }])
                }
              />
            </>
          ) : (
            draft && (
              <>
                <label>
                  Event type
                  <Select
                    aria-label="Event type"
                    value={draft.type}
                    disabled={calendar.some(r => r.id === draft.id)}
                    onChange={e => {
                      const type = e.target.value as Draft['type'];

                      setDraft({
                        ...blank(day),
                        id: draft.id,
                        title: draft.title,
                        personId: draft.personId,
                        notes: draft.notes,
                        type,
                        reminder: type === 'birthday' ? 7 : 15,
                      });

                      setError('');
                      setOccurrence('');
                    }}
                  >
                    <option value="booking">Appointment / task time</option>
                    <option value="birthday">Birthday</option>
                  </Select>
                </label>
                <label>
                  Title
                  <Input
                    required
                    maxLength={500}
                    autoFocus
                    value={draft.title}
                    onChange={e => setDraft({ ...draft, title: e.currentTarget.value })}
                    placeholder={draft.type === 'birthday' ? 'Whose birthday?' : 'What’s planned?'}
                  />
                </label>
                {draft.type === 'birthday' ? (
                  <div className="calendar-fields">
                    <label>
                      Month
                      <Input
                        type="number"
                        min={1}
                        max={12}
                        required
                        value={draft.month}
                        onChange={e => setDraft({ ...draft, month: Number(e.currentTarget.value) })}
                      />
                    </label>
                    <label>
                      Day
                      <Input
                        type="number"
                        min={1}
                        max={31}
                        required
                        value={draft.day}
                        onChange={e => setDraft({ ...draft, day: Number(e.currentTarget.value) })}
                      />
                    </label>
                  </div>
                ) : (
                  <>
                    <label>
                      Related task
                      <Select
                        aria-label="Related task"
                        value={draft.taskId}
                        onChange={e => {
                          const task = tasks.find(r => r.id === e.currentTarget.value)?.value as
                            | Task
                            | undefined;

                          setDraft({
                            ...draft,
                            taskId: e.currentTarget.value,
                            title: task?.title ?? draft.title,
                          });
                        }}
                      >
                        <option value="">Appointment · no task</option>
                        {draft.taskId && !tasks.some(t => t.id === draft.taskId) && (
                          <option value={draft.taskId} disabled>
                            Removed task · existing link
                          </option>
                        )}
                        {tasks.map(t => (
                          <option key={t.id} value={t.id}>
                            {(t.value as Task).title}
                          </option>
                        ))}
                      </Select>
                    </label>
                    <div className="calendar-fields">
                      <label>
                        Start
                        <Input
                          type="datetime-local"
                          required
                          value={draft.start}
                          onInput={e => {
                            let end = draft.end;

                            if (draft.taskId && duration > 0) {
                              try {
                                end = localInput(
                                  new Date(
                                    Date.parse(localInstant(e.currentTarget.value, occurrence)) +
                                      duration * 60000,
                                  ).toISOString(),
                                );
                              } catch {
                                /* Validate on save. */
                              }
                            }

                            setDraft({ ...draft, start: e.currentTarget.value, end });
                          }}
                        />
                      </label>
                      <label>
                        End
                        <Input
                          type="datetime-local"
                          required
                          value={draft.end}
                          onInput={e => {
                            setDraft({ ...draft, end: e.currentTarget.value });

                            if (draft.taskId) {
                              try {
                                const minutes =
                                  (Date.parse(localInstant(e.currentTarget.value, occurrence)) -
                                    Date.parse(localInstant(draft.start, occurrence))) /
                                  60000;

                                if (minutes > 0) {
                                  setDuration(minutes);
                                }
                              } catch {
                                /* Validate on save. */
                              }
                            }
                          }}
                        />
                      </label>
                    </div>
                    {!!draft.taskId && (
                      <div className="slot-picker">
                        <div className="field-grid">
                          <label>
                            Estimate in minutes
                            <Input
                              type="number"
                              min={1}
                              max={1440}
                              required
                              value={duration}
                              onChange={e => {
                                const minutes = Number(e.currentTarget.value);

                                setDuration(minutes);

                                if (minutes > 0 && Number.isFinite(minutes)) {
                                  try {
                                    const start = localInstant(draft.start, occurrence);

                                    setDraft({
                                      ...draft,
                                      end: localInput(
                                        new Date(Date.parse(start) + minutes * 60000).toISOString(),
                                      ),
                                    });
                                  } catch {
                                    /* User will resolve invalid local times on save. */
                                  }
                                }
                              }}
                            />
                          </label>
                          <label>
                            Find slots on
                            <Input
                              type="date"
                              value={day}
                              onInput={e => {
                                if (e.currentTarget.value) {
                                  setDay(e.currentTarget.value);
                                }
                              }}
                            />
                          </label>
                        </div>
                        <span className="eyebrow">FITTING SLOTS ON {day}</span>
                        {suggestions.length ? (
                          <div className="slot-buttons">
                            {suggestions.map(slot => (
                              <Button
                                variant="secondary"
                                type="button"
                                key={slot.startsAt}
                                onClick={() =>
                                  setDraft({
                                    ...draft,
                                    start: localInput(slot.startsAt),
                                    end: localInput(slot.endsAt),
                                  })
                                }
                              >
                                {time(slot.startsAt)}–{time(slot.endsAt)}
                              </Button>
                            ))}
                          </div>
                        ) : (
                          <div>
                            <p className="muted">
                              No fitting slot. Set availability, choose another day, or shorten the
                              estimate.
                            </p>
                            <Button variant="ghost" type="button" onClick={beginAvailability}>
                              Set availability{' '}
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                    <label>
                      Linked note
                      <Select
                        aria-label="Calendar linked note"
                        value={draft.noteId}
                        onChange={e => setDraft({ ...draft, noteId: e.currentTarget.value })}
                      >
                        <option value="">No note</option>
                        {draft.noteId && !notes.some(n => n.id === draft.noteId) && (
                          <option value={draft.noteId} disabled>
                            Removed note · existing link
                          </option>
                        )}
                        {notes.map(n => (
                          <option key={n.id} value={n.id}>
                            {(n.value as Note).title}
                          </option>
                        ))}
                      </Select>
                    </label>
                  </>
                )}
                <label>
                  Person
                  <Select
                    value={draft.personId}
                    onChange={e => setDraft({ ...draft, personId: e.target.value })}
                  >
                    <option value="">No linked person</option>
                    {records
                      .filter(r => r.kind === 'person')
                      .map(r => (
                        <option value={r.id} key={r.id}>
                          {(r.value as Person).name}
                        </option>
                      ))}
                  </Select>
                </label>
                {draft.personId && (
                  <p className="small-muted">
                    Gift ideas and history are stored on this person’s page.
                  </p>
                )}
                {draft.type === 'booking' &&
                  !draft.taskId &&
                  !draft.recurrence &&
                  !calendar.some(r => r.id === draft.id) && (
                    <>
                      <label>
                        Repeat
                        <Select
                          value={draft.frequency}
                          onChange={e =>
                            setDraft({
                              ...draft,
                              frequency: e.target.value as Draft['frequency'],
                            })
                          }
                        >
                          <option value="">Does not repeat</option>
                          <option value="daily">Daily</option>
                          <option value="weekly">Weekly</option>
                          <option value="monthly">Monthly</option>
                          <option value="yearly">Yearly</option>
                        </Select>
                      </label>
                      {draft.frequency && (
                        <div className="field-grid">
                          <label>
                            Every
                            <Input
                              type="number"
                              min={1}
                              max={52}
                              value={draft.repeatInterval}
                              onChange={e =>
                                setDraft({ ...draft, repeatInterval: Number(e.target.value) })
                              }
                            />
                          </label>
                          <label>
                            Occurrences
                            <Input
                              type="number"
                              min={1}
                              max={366}
                              value={draft.repeatCount}
                              onChange={e =>
                                setDraft({ ...draft, repeatCount: Number(e.target.value) })
                              }
                            />
                          </label>
                        </div>
                      )}
                      <p className="small-muted">
                        Repeats keep their local start time. Missing month dates are skipped.
                        Nonexistent clock times require adjusting the series before saving.
                      </p>
                    </>
                  )}
                {draft.recurrence && (
                  <>
                    <Button
                      variant="ghost"
                      type="button"
                      onClick={async () => {
                        try {
                          const changes = bookings
                            .filter(
                              r =>
                                r.value.recurrence?.seriesId === draft.recurrence?.seriesId &&
                                r.value.recurrence!.index >= draft.recurrence!.index,
                            )
                            .map(r => ({ ...r, value: { ...r.value, cancelled: true } }));

                          await workspace.saveMany(changes);
                          setDraft(null);

                          setNotice(
                            'This occurrence and the remaining series were cancelled. They remain in Cancelled items.',
                          );
                        } catch (e) {
                          setError((e as Error).message);
                        }
                      }}
                    >
                      Cancel this and later occurrences
                    </Button>
                    <p className="notice">
                      Occurrence {draft.recurrence.index + 1} of {draft.recurrence.count}. Changes
                      and cancellation apply to this occurrence; the rest of the series stays as
                      planned.
                    </p>
                  </>
                )}
                <label>
                  {draft.type === 'birthday'
                    ? 'Remind me this many days ahead'
                    : 'Remind me this many minutes before'}
                  <Input
                    type="number"
                    min={0}
                    max={draft.type === 'birthday' ? 365 : 10080}
                    value={draft.reminder}
                    onChange={e => setDraft({ ...draft, reminder: Number(e.currentTarget.value) })}
                    required
                  />
                </label>
                <label>
                  {draft.type === 'birthday' ? 'Gift ideas and context' : 'Appointment context'}
                  <Textarea
                    rows={3}
                    value={draft.notes}
                    onChange={e => setDraft({ ...draft, notes: e.currentTarget.value })}
                  />
                </label>
              </>
            )
          )}
          {(availabilityOpen || draft?.type === 'booking') && (
            <Disclosure className="clock-occurrence" summary="Timezone and repeated clock times">
              <p>{zone}. For daylight-saving clock repeats, explicitly choose an occurrence.</p>
              <Select
                aria-label="Repeated clock occurrence"
                value={occurrence}
                onChange={e => setOccurrence(e.currentTarget.value as typeof occurrence)}
              >
                <option value="">Ask if the clock time repeats</option>
                <option value="earlier">First occurrence</option>
                <option value="later">Second occurrence</option>
              </Select>
            </Disclosure>
          )}
          {error && (
            <p className="error-message" role="alert">
              {error}
            </p>
          )}
          <div className="button-row wrap">
            <Button variant="primary" disabled={saving}>
              {saving ? 'Saving…' : availabilityOpen ? 'Save availability' : 'Save event'}
            </Button>
            {draft && calendar.some(r => r.id === draft.id) && (
              <ActionIconButton
                action={draft.cancelled ? 'restore' : 'cancelEvent'}
                label={draft.cancelled ? 'Restore event' : 'Cancel event'}
                type="button"
                onClick={() => {
                  const record = records.find(r => r.id === draft.id)!;

                  void setCancelled(record, !draft.cancelled);
                }}
              />
            )}
          </div>
        </Modal>
      )}
    </main>
  );
}
